import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { Database, FileText, Filter, Search, Settings, Download } from 'lucide-react';
import ExceptionReportModal from '../ExceptionReportModal';
import { useFilter } from '../../contexts/FilterContext';
import { apiFetch } from '../../utils/apiUtils';

const SystemSummary = styled.div`
  margin-bottom: 1.5rem;
`;

const SummaryTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #e0e0e0;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
`;

const SummaryCard = styled.div`
  padding: 1rem;
  text-align: center;
`;

const SummaryValue = styled.div`
  font-size: 1.5rem;
  font-weight: 400;
  color: #ffffff;
  margin-bottom: 0.25rem;
  line-height: 1.2;
`;

const SummaryLabel = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: 500;
  line-height: 1.2;
`;

const FiltersSection = styled.div`
  margin-bottom: 1.5rem;
`;

const FiltersTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #e0e0e0;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.5rem;
  background: #2d2d2d;
  border: 1px solid #4a4a4a;
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
  
  &::placeholder {
    color: #6b7280;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
`;

const FilterButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.active ? '#4a4a4a' : '#2d2d2d'};
  border: 1px solid ${props => props.active ? '#6a6a6a' : '#4a4a4a'};
  border-radius: 4px;
  color: ${props => props.active ? '#ffffff' : '#a0a0a0'};
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#5a5a5a' : '#3a3a3a'};
    border-color: ${props => props.active ? '#7a7a7a' : '#6a6a6a'};
  }
`;

const ManageButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: #4a4a4a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: #5a5a5a;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

function SystemFilesContent() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({
    totalRecords: 0,
    totalFields: 0,
    totalLoans: 0,
    lastImport: null
  });
  const { searchQuery: globalSearchQuery, setSearchQuery: setGlobalSearchQuery } = useFilter();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [downloadingMissingRecords, setDownloadingMissingRecords] = useState(false);
  const [showThresholdOptions, setShowThresholdOptions] = useState(false);
  const [showExceptionReportModal, setShowExceptionReportModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Force close modal function
  const forceCloseModal = () => {
    setShowExceptionReportModal(false);
    setPreviewData(null);
    setLoadingPreview(false);
    setDownloadingMissingRecords(false);
  };

  // Fetch system summary data
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await apiFetch('/system-files/summary');
        if (response.ok) {
          const data = await response.json();
          setSummary({
            totalRecords: data.total_records || 0,
            totalFields: data.unique_field_names || 0,
            totalLoans: data.total_loans || 0,
            lastImport: data.last_import_date
          });
        }
      } catch (error) {
        console.error('Error fetching system summary:', error);
      }
    };

    fetchSummary();
  }, []);

  // Cleanup modal state on unmount
  useEffect(() => {
    return () => {
      forceCloseModal();
    };
  }, []);

  // Global keyboard shortcut to force close modal (Ctrl+Shift+M)
  useEffect(() => {
    const handleGlobalKeydown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'M') {
        e.preventDefault();
        forceCloseModal();
        console.log('Modal force closed via keyboard shortcut');
      }
    };

    document.addEventListener('keydown', handleGlobalKeydown);
    return () => document.removeEventListener('keydown', handleGlobalKeydown);
  }, []);

  // Make forceCloseModal available globally for debugging
  useEffect(() => {
    window.forceCloseModal = forceCloseModal;
    return () => {
      delete window.forceCloseModal;
    };
  }, []);

  const handleManageFiles = () => {
    // Navigate to the add-data tab
    navigate('/system-files?tab=add-data');
  };

  const handleModalGenerateReport = async (threshold) => {
    setShowExceptionReportModal(false);
    await handleDownloadMissingRecords(threshold);
  };

  const fetchPreviewData = async () => {
    setLoadingPreview(true);
    try {
      const response = await apiFetch('/system-files/missing-records-preview');
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        setShowExceptionReportModal(true);
      } else {
        console.error('Failed to fetch preview data');
        // Don't show modal on error
      }
    } catch (error) {
      console.error('Error fetching preview data:', error);
      // Don't show modal on error
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownloadMissingRecords = async (threshold = 20.0) => {
    setDownloadingMissingRecords(true);
    try {
      const response = await fetch(`/api/system-files/export-missing-records?threshold=${threshold}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        
        // Check if it's a JSON response (no matches found)
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (data.success && data.all_fields_have_coverage) {
            setShowExceptionReportModal(true);
            return;
          }
          if (data.success && data.options) {
            setShowThresholdOptions(true);
            return;
          }
        }
        
        // It's an Excel file - download it
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `core-exception-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Failed to download missing records');
        // You could add a notification here if you have a notification system
      }
    } catch (error) {
      console.error('Error downloading missing records:', error);
    } finally {
      setDownloadingMissingRecords(false);
    }
  };

  const filters = [
    { key: 'all', label: 'All Files' },
    { key: 'recent', label: 'Recent' },
    { key: 'large', label: 'Large Files' },
    { key: 'small', label: 'Small Files' }
  ];

  return (
    <div>
      <SystemSummary>
        <SummaryTitle>
          <Database size={16} />
          System Overview
        </SummaryTitle>
        <SummaryGrid>
          <SummaryCard>
            <SummaryValue>{summary.totalRecords.toLocaleString()}</SummaryValue>
            <SummaryLabel>Records</SummaryLabel>
          </SummaryCard>
          <SummaryCard>
            <SummaryValue>{summary.totalFields}</SummaryValue>
            <SummaryLabel>Fields</SummaryLabel>
          </SummaryCard>
          <SummaryCard>
            <SummaryValue>{summary.totalLoans}</SummaryValue>
            <SummaryLabel>Loans</SummaryLabel>
          </SummaryCard>
        </SummaryGrid>
      </SystemSummary>

      <FiltersSection>
        <FiltersTitle>
          <Filter size={16} />
          Filters & Search
        </FiltersTitle>
        
        <SearchContainer>
          <SearchIcon>
            <Search size={16} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search files, fields, or records..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setGlobalSearchQuery(e.target.value);
            }}
          />
        </SearchContainer>

        <FilterButtons>
          {filters.map(filter => (
            <FilterButton
              key={filter.key}
              active={activeFilter === filter.key}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
            </FilterButton>
          ))}
        </FilterButtons>
      </FiltersSection>

      <ManageButton onClick={handleManageFiles}>
        <Settings size={16} />
        Manage System Files
      </ManageButton>

      <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#2a2a2a', borderRadius: '6px', border: '1px solid #4a4a4a' }}>
        <div style={{ color: '#e0e0e0', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
          Core Exception Report
        </div>
        <div style={{ color: '#9ca3af', fontSize: '0.75rem', lineHeight: '1.4', marginBottom: '0.75rem' }}>
          Generates an Excel report of enabled fields with low coverage percentages. 
          Click to preview available thresholds and see how many fields would be included in each report.
        </div>
        <ManageButton 
          onClick={fetchPreviewData} 
          disabled={loadingPreview || downloadingMissingRecords}
          style={{ marginTop: '0' }}
        >
          <Download size={16} />
          {loadingPreview ? 'Loading...' : downloadingMissingRecords ? 'Generating...' : 'Core Exception Report'}
        </ManageButton>
        
        {showThresholdOptions && (
          <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#1a1a1a', borderRadius: '4px', border: '1px solid #4a4a4a' }}>
            <div style={{ color: '#e0e0e0', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
              No fields found with 20%+ placeholder values. Try a lower threshold:
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {[5, 10, 15].map(threshold => (
                <button
                  key={threshold}
                  onClick={() => {
                    setShowThresholdOptions(false);
                    handleDownloadMissingRecords(threshold);
                  }}
                  disabled={downloadingMissingRecords}
                  style={{
                    padding: '0.25rem 0.5rem',
                    background: '#4a4a4a',
                    border: '1px solid #6a6a6a',
                    borderRadius: '3px',
                    color: '#e0e0e0',
                    fontSize: '0.65rem',
                    cursor: 'pointer'
                  }}
                >
                  {threshold}%
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowThresholdOptions(false)}
              style={{
                marginTop: '0.5rem',
                padding: '0.25rem 0.5rem',
                background: 'transparent',
                border: '1px solid #6a6a6a',
                borderRadius: '3px',
                color: '#9ca3af',
                fontSize: '0.65rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <ExceptionReportModal
        isOpen={showExceptionReportModal}
        onClose={() => setShowExceptionReportModal(false)}
        onGenerateReport={handleModalGenerateReport}
        isGenerating={downloadingMissingRecords}
        previewData={previewData}
      />
    </div>
  );
}

export default SystemFilesContent;
